import type { ComponentProps } from 'react';
import { asWidgetProps } from 'Src/types/widget-helpers';
import React, { forwardRef, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { usePageContext } from 'Context/PageContext';
import { getFromLocalStorage, setInLocalStorage } from 'Client/local-storage-api';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import type { PromoBannerProps } from './promo-banner/PromoBanner';
import { PromoBanner } from './promo-banner/PromoBanner';
const DISABLE_PROMO_BANNER = 'disablePromotionalBanner';
const MOCK_ORIGIN = 'https://mock-origin.clubmed.fr';
type Props = {
  callToAction: PromoBannerProps['callToAction'];
  className?: PromoBannerProps['className'];
  closeLabel: PromoBannerProps['closeLabel'];
  description: PromoBannerProps['description'];
  link: PromoBannerProps['link'];
  title: PromoBannerProps['title'];
  // a page is either the code of a village ("AGAC") or an url ("/")
  enabledPages?: ReadonlyArray<string>;
  disabledPages?: ReadonlyArray<string>;
};
const ONE_DAY = 1000 * 60 * 60 * 24;

function shouldBeDisabled() {
  const data = getFromLocalStorage(DISABLE_PROMO_BANNER);

  if (data == null) {
    return false;
  }

  return data === true;
}

export const PromotionalBanner = forwardRef<HTMLElement, Props>(function PromotionalBanner(
  { enabledPages = [], disabledPages = [], title, description, ...props },
  ref,
) {
  const { asPath } = useRouter();
  const { pageId } = usePageContext();
  const track = useTracking();
  const { pathname } = new URL(asPath, MOCK_ORIGIN);

  const pageMatch = (pages) => {
    return pages.some(
      (page) =>
        page && // testing pages that correspond to a resort/tour
        (pageId === `resort-${page.toUpperCase()}` ||
          pageId === `tour-${page.toUpperCase()}` || // pages that correspond to a url
          pathname === page ||
          pathname.startsWith(`${page}/`)),
    );
  };

  const isPageEnabled = enabledPages.length === 0 || pageMatch(enabledPages);
  const isPageDisabled = disabledPages.length > 0 && pageMatch(disabledPages);
  const shouldBeDisplayed = !shouldBeDisabled() && isPageEnabled && !isPageDisabled;
  const [isDisplayed, setDisplay] = useState(shouldBeDisplayed);
  useEffect(() => {
    setDisplay(shouldBeDisplayed);
  }, [shouldBeDisplayed]);

  const onClose = () => {
    setDisplay(false);
    setInLocalStorage(DISABLE_PROMO_BANNER, true, {
      ttl: ONE_DAY,
    });
    track({
      event: 'eventGA',
      categoryGA: 'promoBanner',
      labelGA: title,
      actionGA: 'close',
      valueGA: '0',
      nonInteraction: '0',
    });
  };

  if (!title || !description || !isDisplayed) {
    return null;
  }

  return (
    <PromoBanner
      {...asWidgetProps<ComponentProps<typeof PromoBanner>>({
        ...props,
        description: description,
        innerRef: ref,
        onClick: () =>
          track({
            event: 'eventGA',
            categoryGA: 'promoBanner',
            labelGA: title,
            actionGA: 'click',
            valueGA: '0',
            nonInteraction: '0',
          }),
        onClose: onClose,
        title: title,
      })}
    />
  );
});
